import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import ScrollableStepper from './ScrollableStepper';
import CopyableText from './CopyableText';
import usePermissions from '../../../Hooks/usePermissions';
import { cotizacionRowCSS, stepperTooltipCSS } from './Cotizaciones.css';

const STATUS_TONE = {
    docs_pending: { ink: '#94650A', soft: '#FDF6E3', dot: '#E5B547' },
    analysis:     { ink: '#214B7B', soft: '#EAF1F8', dot: '#5A8FC7' },
    ocular:       { ink: '#0E6E8E', soft: '#E3F4FA', dot: '#34C6F1' },
    quoting:      { ink: '#1565C0', soft: '#E3EEFC', dot: '#3B82C4' },
    paid:         { ink: '#1F8A5B', soft: '#E2F5EB', dot: '#22C083' },
    invoiced:     { ink: '#7A3A0C', soft: '#FBEEE0', dot: '#D97433' },
    contract_step:{ ink: '#5B3A8E', soft: '#F0EAF8', dot: '#8B5CC7' },
    insurance:    { ink: '#0E6E8E', soft: '#E3F4FA', dot: '#34C6F1' },
    plates:       { ink: '#7A3A0C', soft: '#FBEEE0', dot: '#D97433' },
    rejected:     { ink: '#9B2C2C', soft: '#FBEAEA', dot: '#DC4A4A' },
    contract:     { ink: '#0F5132', soft: '#DCF1E4', dot: '#1FB874' },
    hidden:       { ink: '#475569', soft: '#EEF2F6', dot: '#94A3B8' },
    approved:     { ink: '#0F5132', soft: '#DCF1E4', dot: '#1FB874' },
    in_progress:  { ink: '#1A7C9C', soft: '#E3F4FA', dot: '#34C6F1' },
    cancelled:    { ink: '#475569', soft: '#EEF2F6', dot: '#94A3B8' },
};

const formatPrice = (value) => {
    if (value === null || value === undefined) return '—';
    try {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            maximumFractionDigits: 0,
        }).format(value);
    } catch (e) {
        return `$${Math.round(value).toLocaleString('es-MX')}`;
    }
};

const formatDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch (e) {
        return null;
    }
};

const DOC_LABELS = {
    identifications:      'Identificación oficial',
    acta_constitutiva:    'Acta constitutiva',
    poderes:              'Poderes notariales',
    edos_cta:             'Estados de cuenta',
    estados_financieros:  'Estados financieros',
    references:           'Referencias',
    csf:                  'Constancia de situación fiscal',
    address_proof:        'Comprobante de domicilio',
    address:              'Comprobante de domicilio',
};

const buildPopupContent = (status, actionDetail, raw) => {
    const sections = Array.isArray(raw?.docs_sections) ? raw.docs_sections : null;

    if (status === 'docs_pending') {
        if (sections && sections.length > 0) {
            return {
                type: 'doc_sections',
                title: 'Documentos por sección',
                sections,
            };
        }
        // Fallback al shape plano viejo.
        const missing = Array.isArray(raw?.missing_docs) ? raw.missing_docs : [];
        if (missing.length === 0) return null;
        const counts = raw?.docs_counts ?? {};
        return {
            type: 'missing_docs',
            title: 'Documentos pendientes',
            items: missing.map((code) => ({
                code,
                label: DOC_LABELS[code] ?? code,
                count: counts[code]?.count ?? 0,
                min:   counts[code]?.min ?? 1,
            })),
        };
    }
    if (status === 'rejected' && actionDetail && actionDetail.length > 60) {
        return { type: 'text', title: 'Motivo de rechazo', content: actionDetail };
    }
    return null;
};

const SECTION_STATUS_META = {
    complete: { icon: 'pi pi-check-circle', color: '#16a34a', label: 'Completa' },
    warning:  { icon: 'pi pi-exclamation-triangle', color: '#d97706', label: 'Con observaciones' },
    missing:  { icon: 'pi pi-times-circle', color: '#b91c1c', label: 'Pendiente' },
};

const FIELD_STATE_META = {
    ok:               { color: '#16a34a', bg: '#dcfce7' },
    pending:          { color: '#b91c1c', bg: '#fee2e2' },
    optional_pending: { color: '#d97706', bg: '#fef3c7' },
};

const CotizacionRow = ({ row, onClick, extraActions }) => {
    const { userId, isAdminPei, isVendedorPei, isAdminTrim } = usePermissions();
    const {
        client = {},
        advisor,
        creator,
        unit,
        status,
        status_label: statusLabel,
        action_text: actionText,
        action_detail: actionDetail,
        price,
        monthly_rent: monthlyRent,
        term,
        source,
        steps = [],
        created_at: createdAt,
        has_requote: hasRequote,
        requote_at: requoteAt,
        persona_subtype: personaSubtype,
        tipo_persona: tipoPersona,
        phone: rowPhone,
    } = row || {};
    const phone = client.phone ?? rowPhone ?? null;

    const tone = STATUS_TONE[status] || STATUS_TONE.analysis;
    const isAdminViewer = isAdminPei() || isAdminTrim();
    // Si ya hay vendedor asignado, ese dato debe tener prioridad sobre creator.
    // Solo admins ven esta info.
    const showAdvisor = isAdminViewer && !!advisor;
    const showCreator = isAdminViewer && !advisor && !!creator;

    // Badge de tipo de persona — solo visible para Admin Pei y Vendedor Pei.
    const showPersonaChip = isAdminPei() || isVendedorPei();
    const personaChip = (() => {
        if (!showPersonaChip) return null;
        const code = personaSubtype || tipoPersona;
        if (!code) return null;
        const map = {
            PF:   { label: 'PF',   tone: 'pf'   },
            PFAE: { label: 'PFAE', tone: 'pfae' },
            PM:   { label: 'PM',   tone: 'pm'   },
        };
        return map[code] || null;
    })();

    const [isExpanded, setIsExpanded] = useState(false);

    const requoteTooltip = hasRequote
        ? (requoteAt
            ? `Hay una re-cotización en revisión desde ${formatDate(requoteAt) ?? 'fecha desconocida'}. Debes volver a confirmar la nueva cotización con el cliente para reanudar el proceso.`
            : 'Hay una re-cotización en revisión. Debes volver a confirmar la nueva cotización con el cliente para reanudar el proceso.')
        : null;

    const date = formatDate(createdAt);
    const showTrimChip = isAdminPei() && source === 'trim';
    const clientUserId = client.id ?? row?.user_id ?? null;

    const [requoteHover, setRequoteHover] = useState(null); // { x, y }
    const [rowHovered, setRowHovered] = useState(false);
    const [detailPopup, setDetailPopup] = useState(null); // { x, y, anchorTop }
    const popupRef = useRef(null);

    useLayoutEffect(() => {
        if (!detailPopup || !popupRef.current) return;
        const el = popupRef.current;
        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const margin = 8;

        let newX = detailPopup.x;
        let newY = detailPopup.y;

        if (rect.right > vw - margin) {
            newX = Math.max(margin, vw - rect.width - margin);
        }
        if (newX < margin) newX = margin;

        if (rect.bottom > vh - margin) {
            const aboveY = (detailPopup.anchorTop ?? detailPopup.y) - rect.height - 6;
            if (aboveY >= margin) {
                newY = aboveY;
            } else {
                newY = Math.max(margin, vh - rect.height - margin);
            }
        }

        if (newX !== detailPopup.x || newY !== detailPopup.y) {
            setDetailPopup((prev) => (prev ? { ...prev, x: newX, y: newY } : prev));
        }
    }, [detailPopup]);

    useEffect(() => {
        if (!requoteHover) return undefined;
        const onScroll = () => setRequoteHover(null);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [requoteHover]);

    useEffect(() => {
        if (!detailPopup) return undefined;
        const closeOnScroll = (e) => {
            if (popupRef.current && e.target instanceof Node && popupRef.current.contains(e.target)) return;
            setDetailPopup(null);
        };
        const closeOnClick = (e) => {
            if (popupRef.current && e.target instanceof Node && popupRef.current.contains(e.target)) return;
            e.stopPropagation();
            setDetailPopup(null);
        };
        window.addEventListener('scroll', closeOnScroll, true);
        window.addEventListener('click', closeOnClick, true);
        return () => {
            window.removeEventListener('scroll', closeOnScroll, true);
            window.removeEventListener('click', closeOnClick, true);
        };
    }, [detailPopup]);

    const DETAIL_LIMIT = 60;
    const detailTruncated = actionDetail && actionDetail.length > DETAIL_LIMIT
        ? actionDetail.slice(0, DETAIL_LIMIT).trimEnd() + '…'
        : actionDetail;

    const popupContent = buildPopupContent(status, actionDetail, row?._raw);
    const detailNeedsPopup = !!popupContent;
    // Conteo de documentos pendientes para mostrar como hint en la celda
    // de acción cuando el backend no envía action_detail (caso Trim).
    const pendingDocsCount = (() => {
        if (!popupContent) return 0;
        if (popupContent.type === 'missing_docs') {
            return popupContent.items.filter((i) => (i.count ?? 0) < (i.min ?? 1)).length;
        }
        if (popupContent.type === 'doc_sections') {
            // Contamos secciones marcadas como 'missing' (etiqueta "Pendiente"
            // en SECTION_STATUS_META) — coincide con lo que el usuario ve en
            // el popup como bloques con bullet rojo.
            return (popupContent.sections ?? []).filter((s) => s.status === 'missing').length;
        }
        return 0;
    })();
    const pendingDocsLabel = pendingDocsCount > 0
        ? `${pendingDocsCount} documento${pendingDocsCount === 1 ? '' : 's'} pendiente${pendingDocsCount === 1 ? '' : 's'}`
        : null;

    const showDetailPopup = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDetailPopup({ x: rect.left, y: rect.bottom + 6, anchorTop: rect.top });
    };

    const showRequoteTooltip = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setRequoteHover({
            x: rect.left + rect.width / 2,
            y: rect.top - 8,
        });
    };
    const hideRequoteTooltip = () => setRequoteHover(null);

    const handleChevronClick = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    const handleRowClick = (e) => {
        if (onClick) {
            onClick(e);
        }
    };

    return (
        <div
            css={cotizacionRowCSS(status, isExpanded)}
            onClick={handleRowClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(e);
                }
            }}
            onMouseEnter={() => setRowHovered(true)}
            onMouseLeave={() => setRowHovered(false)}
        >
            {/* ── COLLAPSED HEADER (Mobile-only through CSS) ──────────── */}
            <div className="cr-header">
                <div className="cr-info">
                    <div className="cr-info-top">
                        <span className="cr-name">{client.name || client.email || 'Cliente sin nombre'}</span>
                        {personaChip && (
                            <span className={`cr-persona-mini cr-persona-mini-${personaChip.tone}`}>
                                {personaChip.label}
                            </span>
                        )}
                        {showTrimChip && (
                            <span className="cr-source-chip cr-source-trim">
                                <i className="pi pi-tag" style={{ fontSize: '0.6rem' }} />
                                TRIM
                            </span>
                        )}
                    </div>
                    {(client.email || clientUserId != null) && (
                        <span className="cr-meta">
                            {client.email && (
                                <CopyableText
                                    value={client.email}
                                    label="Correo"
                                    className="cr-meta-copy"
                                >
                                    {client.email}
                                </CopyableText>
                            )}
                            {clientUserId != null && (
                                <CopyableText
                                    value={clientUserId}
                                    label="ID"
                                    className="cr-meta-copy cr-meta-id"
                                    title={`Copiar ID #${clientUserId}`}
                                >
                                    <span className="cr-meta-uid">#{clientUserId}</span>
                                </CopyableText>
                            )}
                        </span>
                    )}
                    {phone && (
                        <span className="cr-meta cr-meta-phone">
                            <CopyableText value={phone} label="Teléfono" className="cr-meta-copy">
                                {phone}
                            </CopyableText>
                        </span>
                    )}
                </div>

                <div className="cr-car-summary">
                    {unit && <span className="cr-car-unit">{unit}</span>}
                    {price && <span className="cr-price-value">{formatPrice(price)}</span>}
                </div>

                <div 
                    className="cr-toggle-icon"
                    onClick={handleChevronClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleChevronClick(e);
                        }
                    }}
                >
                    <motion.i 
                        className="pi pi-chevron-down"
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                    />
                </div>
            </div>

            {/* ── EXPANDED BODY (Mobile-only through CSS) ─────────────── */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        className="cr-expanded-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="cr-details-grid">
                            <div className="cr-detail-item">
                                <span className="cr-detail-label">Mensualidad</span>
                                <span className="cr-detail-value">
                                    <i className="pi pi-wallet" /> {formatPrice(monthlyRent)}<small>/mes</small>
                                </span>
                            </div>
                            <div className="cr-detail-item">
                                <span className="cr-detail-label">Plazo</span>
                                <span className="cr-detail-value">
                                    <i className="pi pi-calendar" /> {term} meses
                                </span>
                            </div>
                            <div className="cr-detail-item">
                                <span className="cr-detail-label">Fecha</span>
                                <span className="cr-detail-value">
                                    <i className="pi pi-calendar-plus" /> {date || '—'}
                                </span>
                            </div>
                            {showAdvisor ? (
                                <div className="cr-detail-item">
                                    <span className="cr-detail-label">Vendedor</span>
                                    <span className="cr-detail-value cr-advisor-value">
                                        <i className="pi pi-user" />
                                        <span className={Number(advisor.id) === Number(userId) ? 'cr-me' : ''}>
                                            {Number(advisor.id) === Number(userId) ? 'mí' : advisor.name}
                                        </span>
                                    </span>
                                </div>
                            ) : showCreator ? (
                                <div className="cr-detail-item">
                                    <span className="cr-detail-label">Hecha por</span>
                                    <span className="cr-detail-value cr-advisor-value">
                                        <i className="pi pi-pencil" />
                                        <span className={Number(creator.id) === Number(userId) ? 'cr-me' : ''}>
                                            {Number(creator.id) === Number(userId) ? 'mí' : `${creator.name} - ${creator.role_label}`}
                                        </span>
                                    </span>
                                </div>
                            ) : (
                                <div className="cr-detail-item">
                                    <span className="cr-detail-label">Vendedor</span>
                                    <span className="cr-detail-value cr-advisor-value">
                                        <i className="pi pi-user" />
                                        <span className="cr-unassigned">Sin asignar</span>
                                    </span>
                                </div>
                            )}
                        </div>

                        <div
                            className={`cr-action-box${hasRequote ? ' has-requote' : ''}${detailNeedsPopup ? ' cr-action-clickable' : ''}`}
                            onMouseEnter={hasRequote ? showRequoteTooltip : undefined}
                            onMouseLeave={hideRequoteTooltip}
                            tabIndex={hasRequote ? 0 : undefined}
                            onClick={detailNeedsPopup ? showDetailPopup : (e) => e.stopPropagation()}
                        >
                            <span className="cr-action-main">{actionText || '—'}</span>
                            {hasRequote && (
                                <span className="cr-requote-chip">
                                    <i className="pi pi-refresh" style={{ fontSize: '0.6rem' }} />
                                    RE-COTIZACIÓN
                                </span>
                            )}
                            {(actionDetail || detailNeedsPopup) && (
                                <span className="cr-action-detail">
                                    {detailTruncated}
                                    {!actionDetail && pendingDocsLabel && (
                                        <span className="cr-pending-count">{pendingDocsLabel}</span>
                                    )}
                                    {detailNeedsPopup && (
                                        <span className="cr-detail-more">ver más</span>
                                    )}
                                </span>
                            )}
                        </div>

                        {extraActions && (
                            <div className="cr-actions-footer">
                                {extraActions}
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Stepper Always Visible on Mobile Card */}
            <div className="cr-mobile-stepper" onClick={(e) => e.stopPropagation()}>
                <ScrollableStepper steps={steps} requote={hasRequote} width={240} />
            </div>


            {/* Desktop grid view (Hidden on mobile through CSS) */}
            <div className="cr-desktop-grid">
                <div className="cr-info">
                    <div className="cr-info-top">
                        <span className="cr-name">{client.name || client.email || 'Cliente sin nombre'}</span>
                        {personaChip && (
                            <span className={`cr-persona-mini cr-persona-mini-${personaChip.tone}`}>
                                {personaChip.label}
                            </span>
                        )}
                        {showTrimChip && (
                            <span className="cr-source-chip cr-source-trim">
                                <i className="pi pi-tag" style={{ fontSize: '0.6rem' }} />
                                TRIM
                            </span>
                        )}
                    </div>
                    {(client.email || clientUserId != null) && (
                        <span className="cr-meta">
                            {client.email && (
                                <CopyableText
                                    value={client.email}
                                    label="Correo"
                                    className="cr-meta-copy"
                                >
                                    {client.email}
                                </CopyableText>
                            )}
                            {clientUserId != null && (
                                <CopyableText
                                    value={clientUserId}
                                    label="ID"
                                    className="cr-meta-copy cr-meta-id"
                                    title={`Copiar ID #${clientUserId}`}
                                >
                                    <span className="cr-meta-uid">#{clientUserId}</span>
                                </CopyableText>
                            )}
                        </span>
                    )}
                    {phone && (
                        <span className="cr-meta cr-meta-phone">
                            <CopyableText value={phone} label="Teléfono" className="cr-meta-copy">
                                {phone}
                            </CopyableText>
                        </span>
                    )}
                    {showAdvisor && (
                        <div className="cr-advisor-chip">
                            <i className="pi pi-user" style={{ fontSize: '0.65rem' }} />
                            <span>
                                {Number(advisor.id) === Number(userId) ? 'Vendedor: mí' : `Vendedor ${advisor.name}`}
                            </span>
                        </div>
                    )}
                    {showCreator && (
                        <div className="cr-advisor-chip" title={`Cotización originada por ${creator.role_label || 'usuario interno'}`}>
                            <i className="pi pi-pencil" style={{ fontSize: '0.65rem' }} />
                            <span>
                                Hecha por {Number(creator.id) === Number(userId) ? 'mí' : `${creator.name.split(' ')[0]} - ${creator.role_label}`}
                            </span>
                        </div>
                    )}
                </div>

                <div className="cr-car-col">
                    {unit && <span className="cr-car-unit">{unit}</span>}
                    {(monthlyRent || term) && (
                        <span className="cr-car-finance">
                            {monthlyRent && (
                                <>
                                    {formatPrice(monthlyRent)}
                                    <span className="cr-car-permes">/mes</span>
                                </>
                            )}
                            {monthlyRent && term && <span className="cr-car-sep"> · </span>}
                            {term && <>{term} meses</>}
                        </span>
                    )}
                </div>

                <div
                    className={`cr-action${hasRequote ? ' has-requote' : ''}${detailNeedsPopup ? ' cr-action-clickable' : ''}`}
                    onClick={detailNeedsPopup ? showDetailPopup : (e) => e.stopPropagation()}
                >
                    <div className="cr-action-line1">
                        <span className="cr-action-dot" style={{ background: tone.dot }} aria-hidden="true" />
                        <span className="cr-action-text" style={{ color: tone.ink }}>{actionText || '—'}</span>
                    </div>
                    {(actionDetail || detailNeedsPopup) && (
                        <span className="cr-action-detail">
                            {detailTruncated}
                            {!actionDetail && pendingDocsLabel && (
                                <span className="cr-pending-count">{pendingDocsLabel}</span>
                            )}
                            {detailNeedsPopup && (
                                <span className="cr-detail-more">ver más</span>
                            )}
                        </span>
                    )}
                </div>

                <div className="cr-price">
                    <span className="cr-price-value">{formatPrice(price)}</span>
                    {date && <span className="cr-price-date">{date}</span>}
                </div>

                <div className="cr-stepper">
                    <ScrollableStepper steps={steps} requote={hasRequote} width={290} />
                </div>

                {extraActions && rowHovered && (
                    <motion.div
                        className="cr-overlay-actions"
                        initial={{ opacity: 0, x: 18, scale: 0.92 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 12, scale: 0.95 }}
                        transition={{ type: 'spring', stiffness: 420, damping: 28, mass: 0.7 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {extraActions}
                    </motion.div>
                )}
            </div>

            {requoteHover && requoteTooltip && typeof document !== 'undefined' && createPortal(
                <div
                    css={stepperTooltipCSS()}
                    style={{ left: requoteHover.x, top: requoteHover.y, maxWidth: 280 }}
                    role="tooltip"
                >
                    {requoteTooltip}
                </div>,
                document.body,
            )}

            {detailPopup && popupContent && typeof document !== 'undefined' && createPortal(
                <div
                    ref={popupRef}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        left: detailPopup.x,
                        top: detailPopup.y,
                        zIndex: 9999,
                        maxWidth: 340,
                        minWidth: 240,
                        maxHeight: 'calc(100vh - 16px)',
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(249,255,251,0.97) 100%)',
                        border: '1px solid rgba(34,236,155,0.35)',
                        borderRadius: 16,
                        boxShadow: '0 10px 40px -20px rgba(34,236,155,0.35), 0 4px 16px -6px rgba(0,0,0,0.1)',
                        padding: '0.9rem 1rem',
                    }}
                >
                    {popupContent.title && (
                        <h4 style={{ margin: '0 0 0.55rem', fontSize: '0.72rem', fontWeight: 700, color: '#1565C0', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {popupContent.title}
                        </h4>
                    )}
                    {popupContent.type === 'text' && (
                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#1e293b', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                            {popupContent.content}
                        </p>
                    )}
                    {popupContent.type === 'missing_docs' && (
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            {popupContent.items.map((item) => (
                                <li key={item.code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem', color: '#1e293b' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                        <i className="pi pi-file" style={{ fontSize: '0.7rem', color: '#94a3b8' }} />
                                        {item.label}
                                    </span>
                                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: item.count >= item.min ? '#16a34a' : '#b91c1c', background: item.count >= item.min ? '#dcfce7' : '#fee2e2', padding: '0.15rem 0.45rem', borderRadius: 999 }}>
                                        {item.count}/{item.min}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {popupContent.type === 'doc_sections' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                            {popupContent.sections.map((section) => {
                                const meta = SECTION_STATUS_META[section.status] || SECTION_STATUS_META.missing;
                                return (
                                    <div key={section.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.74rem', fontWeight: 700, color: meta.color }}>
                                            <i className={meta.icon} style={{ fontSize: '0.78rem' }} />
                                            <span style={{ color: '#1e293b' }}>{section.label}</span>
                                            <span style={{ marginLeft: 'auto', fontSize: '0.65rem', fontWeight: 600, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                {meta.label}
                                            </span>
                                        </div>
                                        <ul style={{ margin: 0, paddingLeft: '1rem', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                            {section.fields.map((field) => {
                                                const fMeta = FIELD_STATE_META[field.state] || FIELD_STATE_META.pending;
                                                return (
                                                    <li key={field.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', color: '#475569' }}>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                                            <i className="pi pi-file" style={{ fontSize: '0.65rem', color: '#94a3b8' }} />
                                                            {field.label}
                                                        </span>
                                                        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: fMeta.color, background: fMeta.bg, padding: '0.12rem 0.4rem', borderRadius: 999 }}>
                                                            {field.count}/{field.min}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>,
                document.body,
            )}
        </div>
    );
};

export default CotizacionRow;
